import React, { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Film, Heart, Play as PlayIcon, Star, ChevronRight, Volume2, VolumeX, Maximize2, X } from "lucide-react";
import api from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const s = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  const w = Math.floor(d / 7);
  if (w < 5) return `${w}w ago`;
  return new Date(iso).toLocaleDateString();
}

export function ClipCard({ clip, linkBase = "/app/arcade", grid = false, rank = null, hoverPlay = false, soundToggle = false, theater = false }) {
  const navigate = useNavigate();
  const [liked, setLiked] = useState(!!clip.liked);
  const [likes, setLikes] = useState(clip.likes || 0);
  const [muted, setMuted] = useState(true);
  const [theaterOpen, setTheaterOpen] = useState(false);
  const playedRef = useRef(false);
  const videoRef = useRef(null);
  const theaterVideoRef = useRef(null);

  const onPlay = () => {
    if (playedRef.current) return;
    playedRef.current = true;
    api.post(`/clips/${clip.id}/play`).catch(() => {});
  };
  const onEnter = () => { if (hoverPlay && videoRef.current) videoRef.current.play().catch(() => {}); };
  const onLeave = () => { if (hoverPlay && videoRef.current) { videoRef.current.pause(); try { videoRef.current.currentTime = 0; } catch { /* ignore */ } } };
  const toggleSound = (e) => {
    e.preventDefault(); e.stopPropagation();
    const v = videoRef.current; if (!v) return;
    v.muted = !v.muted; setMuted(v.muted);
    if (!v.muted) v.play().catch(() => {});
  };
  const openTheater = (e) => {
    e?.preventDefault?.(); e?.stopPropagation?.();
    if (videoRef.current) { videoRef.current.pause(); }
    setTheaterOpen(true); onPlay();
  };

  // Opening the theater is a user gesture → autoplay WITH sound is allowed.
  useEffect(() => {
    if (theaterOpen && theaterVideoRef.current) {
      const v = theaterVideoRef.current;
      v.muted = false;
      v.play().catch(() => {});
    }
  }, [theaterOpen]);
  useEffect(() => {
    if (!theaterOpen) return;
    const onKey = (e) => { if (e.key === "Escape") setTheaterOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [theaterOpen]);

  const toggleLike = async () => {
    const optimistic = !liked;
    setLiked(optimistic); setLikes((n) => n + (optimistic ? 1 : -1));
    try { const { data } = await api.post(`/clips/${clip.id}/like`); setLiked(data.liked); setLikes(data.likes); }
    catch { setLiked(!optimistic); setLikes((n) => n + (optimistic ? -1 : 1)); }
  };

  return (
    <div onMouseEnter={onEnter} onMouseLeave={onLeave} className={`${grid ? "w-full" : "w-[248px] shrink-0"} rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] overflow-hidden card-hover relative`} data-testid={`clip-card-${clip.id}`}>
      {clip.featured && (
        <div className="absolute top-2 left-2 z-10 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-400 text-black flex items-center gap-1 shadow" data-testid={`clip-staffpick-${clip.id}`}>
          <Star className="w-3 h-3 fill-current" /> Staff Pick
        </div>
      )}
      {rank && (
        <div className="absolute top-2 right-2 z-10 px-2 py-0.5 rounded-full text-[11px] font-bold bg-black/75 text-white shadow" data-testid={`clip-rank-${clip.id}`}>
          {rank === 1 ? "🥇" : rank === 2 ? "🥈" : rank === 3 ? "🥉" : `#${rank}`}
        </div>
      )}
      {clip.coop_mvp && (
        <div className="absolute left-2 z-10 px-2 py-0.5 rounded-full text-[10px] font-bold bg-gradient-to-r from-fuchsia-500 to-violet-500 text-white flex items-center gap-1 shadow" style={{ top: clip.featured ? 32 : 8 }} data-testid={`clip-mvp-${clip.id}`}>
          👑 Final Blow
        </div>
      )}
      {theater && !rank && (
        <button onClick={openTheater} data-testid={`clip-expand-${clip.id}`} title="Watch fullscreen"
          className="absolute top-2 right-2 z-10 w-9 h-9 grid place-items-center rounded-full bg-black/70 text-white hover:bg-black/90 transition-colors">
          <Maximize2 className="w-4 h-4" />
        </button>
      )}
      {soundToggle && (
        <button onClick={toggleSound} data-testid={`clip-sound-${clip.id}`} title={muted ? "Unmute" : "Mute"}
          className="absolute bottom-2 right-2 z-10 w-9 h-9 grid place-items-center rounded-full bg-black/70 text-white hover:bg-black/90 transition-colors">
          {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
        </button>
      )}
      <video
        ref={videoRef}
        src={`${BACKEND}${clip.url}`}
        controls muted playsInline preload="metadata"
        onPlay={onPlay}
        className="w-full bg-black block"
        style={{ aspectRatio: "16/9" }}
        data-testid={`clip-card-video-${clip.id}`}
      />
      <div className="p-3 space-y-1.5">
        <p className={`text-[13px] font-semibold leading-snug line-clamp-2 ${clip.caption ? "" : "text-[hsl(var(--muted-foreground))]"}`}>
          {clip.caption || "A wild gameplay moment"}
        </p>
        <button onClick={() => navigate(`${linkBase}/${clip.game_id}`)} data-testid={`clip-card-game-${clip.id}`}
          className="flex items-center gap-1.5 text-xs text-[hsl(var(--primary))] font-semibold hover:underline w-full text-left">
          <span>{clip.cover_emoji || "🎮"}</span>
          <span className="truncate">{clip.game_title || "Play game"}</span>
        </button>
        <div className="flex items-center justify-between text-[11px] text-[hsl(var(--muted-foreground))]">
          <span className="truncate max-w-[130px]">by {clip.uploader_name || "Player"}{clip.created_at ? ` · ${timeAgo(clip.created_at)}` : ""}</span>
          <div className="flex items-center gap-2.5 shrink-0">
            <span className="flex items-center gap-1"><PlayIcon className="w-3 h-3" /> {clip.plays || 0}</span>
            <button onClick={toggleLike} data-testid={`clip-like-${clip.id}`}
              className={`flex items-center gap-1 transition-colors ${liked ? "text-rose-400" : "hover:text-rose-400"}`}>
              <Heart className={`w-3.5 h-3.5 ${liked ? "fill-current" : ""}`} /> {likes}
            </button>
          </div>
        </div>
      </div>
      {theaterOpen && createPortal(
        <div className="fixed inset-0 z-[120] bg-black/95 backdrop-blur-sm flex flex-col items-center justify-center p-4" data-testid={`clip-theater-${clip.id}`} onClick={() => setTheaterOpen(false)}>
          <button onClick={(e) => { e.stopPropagation(); setTheaterOpen(false); }} data-testid={`clip-theater-close-${clip.id}`}
            className="absolute top-4 right-4 w-11 h-11 grid place-items-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors">
            <X className="w-5 h-5" />
          </button>
          <video ref={theaterVideoRef} src={`${BACKEND}${clip.url}`} controls autoPlay playsInline onPlay={onPlay}
            onClick={(e) => e.stopPropagation()}
            className="max-h-[82vh] max-w-[95vw] rounded-2xl shadow-2xl bg-black"
            data-testid={`clip-theater-video-${clip.id}`} />
          <div className="mt-4 text-center text-white max-w-2xl px-2" onClick={(e) => e.stopPropagation()}>
            {clip.coop_mvp && <div className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-gradient-to-r from-fuchsia-500 to-violet-500 mb-2">👑 Final Blow</div>}
            <p className="font-semibold leading-snug">{clip.caption || "A wild gameplay moment"}</p>
            <p className="text-sm text-white/60 mt-0.5">{clip.cover_emoji || "🎮"} {clip.game_title || "GameView"} · by {clip.uploader_name || "Player"}</p>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

// Horizontal rail of community-published gameplay clips. Renders nothing when empty.
// variant="rail" (in-app) | "landing" (public showcase with centered header).
export default function CommunityClips({
  gameId, title = "🎬 Trending Clips", sort = "trending", limit = 12,
  testid = "community-clips", linkBase = "/app/arcade", featured = false, variant = "rail",
  seeAllTo = null,
}) {
  const navigate = useNavigate();
  const [clips, setClips] = useState(null);

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams({ sort, limit: String(limit) });
      if (gameId) params.set("game_id", gameId);
      if (featured) params.set("featured", "true");
      const { data } = await api.get(`/clips?${params.toString()}`);
      setClips(data.clips || []);
    } catch { setClips([]); }
  }, [gameId, sort, limit, featured]);

  useEffect(() => { load(); }, [load]);

  if (!clips || clips.length === 0) return null;

  if (variant === "landing") {
    return (
      <section className="relative z-10 max-w-6xl mx-auto px-5 py-12" data-testid={testid}>
        <div className="flex flex-col items-center text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-400/15 text-amber-300 text-xs font-bold mb-3">
            <Star className="w-3.5 h-3.5 fill-current" /> STAFF PICKS
          </div>
          <h2 className="font-display text-3xl sm:text-4xl font-bold mb-2">{title}</h2>
          <p className="text-white/60 max-w-2xl">Real gameplay moments captured &amp; shared by our players — sound on.</p>
        </div>
        <div className="flex gap-4 overflow-x-auto pb-2 -mx-1 px-1 justify-start sm:justify-center" data-testid={`${testid}-row`}>
          {clips.map((c) => <ClipCard key={c.id} clip={c} linkBase={linkBase} />)}
        </div>
      </section>
    );
  }

  return (
    <div className="mb-8" data-testid={testid}>
      <div className="flex items-center justify-between mb-3 gap-3">
        <h2 className="font-display text-xl font-bold flex items-center gap-2 min-w-0">
          <Film className="w-5 h-5 text-fuchsia-400 shrink-0" /> <span className="truncate">{title}</span>
          <span className="text-xs font-normal text-[hsl(var(--muted-foreground))] hidden sm:inline">shared by players</span>
        </h2>
        {seeAllTo && (
          <button onClick={() => navigate(seeAllTo)} data-testid={`${testid}-see-all`}
            className="text-sm font-semibold text-[hsl(var(--primary))] hover:underline flex items-center gap-0.5 shrink-0">
            See all <ChevronRight className="w-4 h-4" />
          </button>
        )}
      </div>
      <div className="flex gap-4 overflow-x-auto pb-2 -mx-1 px-1" data-testid={`${testid}-row`}>
        {clips.map((c) => <ClipCard key={c.id} clip={c} linkBase={linkBase} />)}
      </div>
    </div>
  );
}
